// Copyright 2024 Sorama B.V.

/**
 * Represents a color with red, green, blue, and alpha components.
 * The rgb components are in the range (0-255), the alpha component is a number between 0 and 1.
 */
export class Color {
    /**
     * Creates an instance of Color.
     * Automatically clamops the values to the valid range.
     * @param r - The red component of the color (0-255).
     * @param g - The green component of the color (0-255).
     * @param b - The blue component of the color (0-255).
     * @param a - The alpha (transparency) component of the color (0-1). (defaults to 1)
     */
    constructor(r: number, g: number, b: number, a = 1) {
        this.r = Math.max(Math.min(r, 255), 0);
        this.g = Math.max(Math.min(g, 255), 0);
        this.b = Math.max(Math.min(b, 255), 0);
        this.a = Math.max(Math.min(a, 1), 0);
    }

    /** The red component of the color. In the range (0-255).*/
    public r: number;
    /** The green component of the color. In the range (0-255).*/
    public g: number;
    /** The blue component of the color. In the range (0-255).*/
    public b: number;
    /** The alpha (transparency) component of the color. In the range (0-1).*/
    public a: number;

    /**
     * Creates a copy of the current color instance.
     * @returns A new Color instance with the same r, g, b, and a values.
     */
    public copy(): Color {
        return new Color(this.r, this.g, this.b, this.a);
    }

    /**
     * Multiplies the color with a factor. Similar to setting brightness.
     * @param factor
     * @returns The new color instance with the scaled values.
     */
    public scale(factor: number): Color {
        return new Color(this.r * factor, this.g * factor, this.b * factor, this.a);
    }

    /**
     * Converts a color to a CSS string.
     * @returns The CSS string representation of the color. (rgba)
     */
    public get css(): string {
        if (this.a === 1) {
            return "rgb(" + Math.round(this.r) + "," + Math.round(this.g) + "," + Math.round(this.b) + ")";
        } else {
            return (
                "rgba(" + Math.round(this.r) + "," + Math.round(this.g) + "," + Math.round(this.b) + "," + this.a + ")"
            );
        }
    }

    /**
     * Converts a color to a hex string.
     * @returns The hex string representation of the color.
     */
    public get hex(): string {
        let r = this.r.toString(16);
        while (r.length < 2) {
            r = "0" + r;
        }

        let g = this.g.toString(16);
        while (g.length < 2) {
            g = "0" + g;
        }

        let b = this.b.toString(16);
        while (b.length < 2) {
            b = "0" + b;
        }

        if (this.a === 1) {
            return "#" + r.substring(0, 2) + g.substring(0, 2) + b.substring(0, 2);
        }

        let a = Math.min(Math.round(this.a * 255), 255).toString(16);
        while (a.length < 2) {
            a = "0" + a;
        }

        return "#" + r.substring(0, 2) + g.substring(0, 2) + b.substring(0, 2) + a.substring(0, 2);
    }

    /**
     * Converts a color to a hsv object.
     * @returns The hsv object representation of the color.
     * h: The hue of the color (0-1).
     * s: The saturation of the color (0-1).
     * v: The value of the color (0-1).
     * @see https://en.wikipedia.org/wiki/HSL_and_HSV
     */
    public get hsv(): { h: number; s: number; v: number } {
        let rr, gg, bb, h, s, v;
        const rNorm = this.r / 255;
        const gNorm = this.g / 255;
        const bNorm = this.b / 255;
        v = Math.max(rNorm, gNorm, bNorm);
        const diff = v - Math.min(rNorm, bNorm, bNorm),
            diffc = (c: number) => {
                return (v - c) / 6 / diff + 1 / 2;
            };

        if (diff == 0) {
            h = s = 0;
        } else {
            s = diff / v;
            rr = diffc(rNorm);
            gg = diffc(gNorm);
            bb = diffc(bNorm);

            if (rNorm === v) {
                h = bb - gg;
            } else if (gNorm === v) {
                h = 1 / 3 + rr - bb;
            } else if (bNorm === v) {
                h = 2 / 3 + gg - rr;
            }
            if (h < 0) {
                h += 1;
            } else if (h > 1) {
                h -= 1;
            }
        }
        return {
            h: h,
            s: s,
            v: v,
        };
    }

    /**
     * Checks if two colors are equal.
     * @param other - The color to compare to.
     * @returns True if the colors are equal, false otherwise.
     */
    public equals(other: Color): boolean {
        return this.r === other.r && this.g === other.g && this.b === other.b && this.a === other.a;
    }

    //#region Static methods (helpers)

    /**
     * Converts a hex string to a color.
     * @param color - The hex string to convert.
     * @returns The color representation of the hex string.
     */
    static fromHex(color: string): Color {
        let adjustableColor = color;
        if (adjustableColor.startsWith("#")) {
            adjustableColor = adjustableColor.substring(1);
        }

        if (adjustableColor.length != 6 && adjustableColor.length != 8) {
            throw new Error("Invalid color format");
        }

        const r = Number.parseInt(adjustableColor.substring(0, 2), 16);
        const g = Number.parseInt(adjustableColor.substring(2, 4), 16);
        const b = Number.parseInt(adjustableColor.substring(4, 6), 16);

        let a = 1;
        if (adjustableColor.length == 8) {
            a = Number.parseInt(adjustableColor.substring(6, 8), 16) / 255;
        }

        return new Color(r, g, b, a);
    }

    /**
     * Converts an hsv color to an rgb color.
     * @param h - The hue of the color (0-1).
     * @param s - The saturation of the color (0-1).
     * @param v - The value of the color (0-1).
     * @returns The rgb representation of the hsv color.
     * @see https://en.wikipedia.org/wiki/HSL_and_HSV
     */
    public static fromHSV(h: number, s: number, v: number): Color {
        let r, g, b, i, f, p, q, t;
        i = Math.floor(h * 6);
        f = h * 6 - i;
        p = v * (1 - s);
        q = v * (1 - f * s);
        t = v * (1 - (1 - f) * s);
        switch (i % 6) {
            case 0:
                r = v;
                g = t;
                b = p;
                break;
            case 1:
                r = q;
                g = v;
                b = p;
                break;
            case 2:
                r = p;
                g = v;
                b = t;
                break;
            case 3:
                r = p;
                g = q;
                b = v;
                break;
            case 4:
                r = t;
                g = p;
                b = v;
                break;
            case 5:
                r = v;
                g = p;
                b = q;
                break;
        }
        return new Color(Math.round(r * 255), Math.round(g * 255), Math.round(b * 255));
    }
    //#endregion

    /**
     * Mixes two colors together.
     * @param colorToMix the color to mix to
     * @param factor the amount of the second color to mix in
     * @returns the mixed color
     */
    public mix(colorToMix: Color, factor: number): Color {
        const inv = 1 - factor;
        return new Color(
            this.r * inv + colorToMix.r * factor,
            this.g * inv + colorToMix.g * factor,
            this.b * inv + colorToMix.b * factor,
            this.a * inv + colorToMix.a * factor
        );
    }

    /**
     * Darkens the color by a factor.
     * @param factor the factor to darken the color by
     * @returns the darkened color
     */
    public darken(factor: number): Color {
        return this.mix(new Color(0, 0, 0), factor);
    }

    /**
     * Lightens the color by a factor.
     * @param factor the factor to lighten the color by
     * @returns the lightened color
     */
    public lighten(factor: number): Color {
        return this.mix(new Color(255, 255, 255), factor);
    }
}
